import { Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { combineLatest, interval, Observable, startWith, Subject, switchMap, tap } from "rxjs";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import { IpMarketItemComponent } from "./ip-market-item/ip-market-item.component";
import { AlertComponent } from "../../../../components/alert/alert.component";
import { LiveUpdateService } from "../../../../../../../shared/src/lib/services/live-update.service";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { BetPlacementService } from "../../../../../../../shared/src/lib/services/bet-placement.service";
import { LoaderService } from "../../../../../../../shared/src/lib/services/loader.service";
import { AsyncPipe } from "@angular/common";
import { InPlayLoaderComponent } from "./in-play-loader/in-play-loader.component";
import { MarketOpsService } from "../../market-layout/market/market-ops.service";
import { NavService } from "../../../../../../../shared/src/lib/services/nav.service";
import { map, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-in-play-markets",
  imports: [IpMarketItemComponent, AlertComponent, MatProgressBarModule, AsyncPipe, InPlayLoaderComponent],
  templateUrl: "./in-play-markets.component.html",
  styleUrl: "./in-play-markets.component.scss",
})
export class InPlayMarketsComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  marketService = inject(MarketService);
  liveUpdateService = inject(LiveUpdateService);
  placementService = inject(BetPlacementService);
  markets: any[] = [];
  marketItems: string[] = [];
  $loading!: Observable<boolean>;
  loader = inject(LoaderService);
  marketOps = inject(MarketOpsService);
  navService = inject(NavService);
  parents: any[] = [];
  betPlacementService = inject(BetPlacementService);
  isLoadingStatus = signal(false);

  ngOnInit() {
    this.$loading = combineLatest([this.loader.isLoading("/bf/in-play-markets"), this.loader.isLoading("/v1/api/navigation_list")]).pipe(
      map(([loadingMarkets, loadingNavigationList]) => loadingMarkets || loadingNavigationList),
    );

    interval(120000)
      .pipe(
        startWith(0),
        switchMap(() => this.getInPlayMarkets()),
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  getInPlayMarkets() {
    this.isLoadingStatus.set(true);
    return this.marketService.fetchInPlayMarkets().pipe(
      tap({
        next: (response: any) => {
          // console.log(response?.data);
          const data = response?.data;
          let inPlayItems: any[] = [];
          let inPlayMessage: any[] = [];
          let openBetsFilterMarkets: any[] = [];
          if (data) {
            // console.log(data);
            Object.keys(data).map((key) => {
              inPlayItems = [...inPlayItems, { key, value: data[key] }];
            });

            // console.log(inPlayItems);
            inPlayItems?.forEach((item) => {
              const markets = item?.value.map((market: any) => `bf_${market?.marketId}`);
              const marketIds = item?.value.map((market: any) => market?.marketId);
              openBetsFilterMarkets = [...openBetsFilterMarkets, ...marketIds];
              inPlayMessage = [...inPlayMessage, ...markets];
            });

            this.marketItems = inPlayMessage;
            this.placementService.setOpenBetFilterParams({
              marketIds: openBetsFilterMarkets?.join(","),
            });

            if (this.marketItems?.length > 0) {
              this.getMarketStatus({ market_ids: this.marketItems?.join(",") }, inPlayItems);
              this.marketOps.setTopics(this.marketItems);
            } else {
              this.isLoadingStatus.set(false);
            }
          } else {
            this.isLoadingStatus.set(false);
          }
        },
        error: (error: any) => {
          console.error(error);
        },
      }),
    );
  }

  getMarketStatus(params: any, inPlayItems: any[]) {
    const qParams = { ...params, statuses: "2,3", return_snapshot: "Y" };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // console.log(response);
          const { snapshot: snapshots = {}, statuses = {} } = response || {};
          // console.log(snapshots);
          // console.log(statuses);
          if (inPlayItems?.length > 0) {
            const openedMarkets = statuses["2"] || [];
            const suspendedMarkets = statuses["3"] || [];
            // console.log("In-Play", this.markets);

            this.markets = inPlayItems
              .map((item: any) => {
                let filteredMarkets = item.value
                  .filter((market: any) => {
                    const marketId = `bf_${market?.marketId}`;
                    return openedMarkets.includes(marketId) || suspendedMarkets.includes(marketId);
                  })
                  .map((market: any) => {
                    const marketId = `bf_${market?.marketId}`;
                    return {
                      ...market,
                      mc: snapshots[marketId],
                      eventName: market.event.name,
                    };
                  });

                return {
                  ...item,
                  value: filteredMarkets,
                };
              })
              .filter((item: any) => item.value.length > 0);
            // console.log("In-Play items: ", this.markets);
            this.isLoadingStatus.set(false);
            this.checkPlacement();
          }
        },
        error: (error) => {
          console.error("Failed to fetch market statuses:", error);
        },
      });
  }

  checkPlacement() {
    let markets: any[] = [];
    this.markets.forEach((group: any) => {
      markets = [...markets, ...group.value];
    });

    this.betPlacementService.currentPlacementConcernsMe(markets, "marketId");
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.marketOps.setTopics([]);
  }
}
